/**
 * Firebase Web SDK - Push Notifications for PWA/Browser
 *
 * SETUP: Add your Firebase config values to .env:
 *   REACT_APP_FIREBASE_API_KEY=...
 *   REACT_APP_FIREBASE_AUTH_DOMAIN=...
 *   REACT_APP_FIREBASE_PROJECT_ID=...
 *   REACT_APP_FIREBASE_STORAGE_BUCKET=...
 *   REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...
 *   REACT_APP_FIREBASE_APP_ID=...
 *   REACT_APP_FIREBASE_VAPID_KEY=...
 *
 * Then run: npm install firebase
 */

import { api } from './api';

// Firebase config from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

const isFirebaseConfigured = () => Boolean(
  firebaseConfig.apiKey
    && firebaseConfig.authDomain
    && firebaseConfig.projectId
    && firebaseConfig.messagingSenderId
    && firebaseConfig.appId
    && VAPID_KEY
    && firebaseConfig.apiKey !== 'your_api_key'
);

const firebaseServiceWorkerUrl = () => {
  const params = new URLSearchParams();
  Object.entries(firebaseConfig).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
};

let messaging: any = null;
let firebaseApp: any = null;

/**
 * Initialise Firebase Web SDK (lazy-loaded).
 * Returns null if Firebase env vars are not configured.
 */
export const initFirebaseWeb = async () => {
  if (firebaseApp) return firebaseApp;
  if (!isFirebaseConfigured()) {
    console.info('[Firebase] Web SDK not configured — skipping. Add REACT_APP_FIREBASE_* env vars.');
    return null;
  }

  try {
    // Dynamic import to avoid bundling Firebase when not configured
    const { initializeApp } = await import('firebase/app');
    const { getMessaging } = await import('firebase/messaging');

    firebaseApp = initializeApp(firebaseConfig);
    messaging = getMessaging(firebaseApp);
    console.info('[Firebase] Web SDK initialised');
    return firebaseApp;
  } catch (error) {
    console.warn('[Firebase] Failed to initialise:', error);
    return null;
  }
};

/**
 * Request notification permission and get FCM token for web/PWA.
 * Registers the service worker and sends the token to the backend.
 */
export const requestWebPushToken = async (): Promise<string | null> => {
  if (!('Notification' in window)) {
    console.info('[Firebase] Notifications not supported in this browser');
    return null;
  }

  if (!isFirebaseConfigured()) {
    console.info('[Firebase] Web push not configured — skipping permission prompt.');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.info('[Firebase] Notification permission denied');
    return null;
  }

  try {
    await initFirebaseWeb();
    if (!messaging) return null;

    // Register service worker with config values injected at build time by CRA.
    const registration = await navigator.serviceWorker.register(firebaseServiceWorkerUrl());

    const { getToken } = await import('firebase/messaging');
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      // Send token to backend
      await api.put('/auth/fcm-token', { token, platform: 'web' });
      console.info('[Firebase] Web push token registered');
      return token;
    }
  } catch (error) {
    console.warn('[Firebase] Failed to get web push token:', error);
  }

  return null;
};

/**
 * Listen for foreground messages (when app is open in browser).
 * Shows a browser notification.
 */
export const onForegroundMessage = async (callback: (payload: any) => void) => {
  if (!messaging) {
    await initFirebaseWeb();
  }
  if (!messaging) return;

  try {
    const { onMessage } = await import('firebase/messaging');
    onMessage(messaging, (payload) => {
      callback(payload);

      // Show browser notification for foreground messages
      if (payload.notification) {
        new Notification(payload.notification.title || 'FamHearth', {
          body: payload.notification.body,
          icon: '/icon-192-nobg.png',
          badge: '/icon-192-nobg.png',
        });
      }
    });
  } catch (error) {
    console.warn('[Firebase] Foreground message listener error:', error);
  }
};
